import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  useTheme,
} from "@mui/material";

type ButtonShape = "rounded" | "square" | "default";
type ButtonBorderColor = "border" | "primary";
type ButtonColor = "primary" | "secondary" | "error" | "info" | "text.primary";

type CustomButtonProps = {
  variant?:
    | "contained"
    | "text"
    | "outlined"
    | "buttonGroup"
    | "buttonGroupSelected";
  size?: "small" | "medium";
  color?: ButtonColor;
  shape?: ButtonShape;
  borderColor?: ButtonBorderColor;
  label?: string;
};

export interface ButtonProps
  extends Omit<MuiButtonProps, "variant" | "size" | "color">,
    CustomButtonProps {}

export const Button = ({
  label,
  children,
  shape = "default",
  borderColor,
  color,
  sx,
  ...rest
}: ButtonProps) => {
  const theme = useTheme();

  const borderRadius = {
    rounded: "100px",
    square: "8px",
    default: "100px",
  }[shape];

  const borderColorValue = borderColor
    ? {
        border: theme.palette.border,
        primary: theme.palette.primary.main,
      }[borderColor]
    : undefined;

  const colorValue =
    color === "text.primary"
      ? theme.palette.text.primary
      : color === "primary"
        ? theme.palette.primary.main
        : undefined;

  // Pass color to MUI if it's a standard color (secondary, error, info)
  const muiColor =
    color === "secondary" || color === "error" || color === "info"
      ? color
      : undefined;

  return (
    <MuiButton
      {...rest}
      color={muiColor}
      sx={{
        borderRadius,
        ...(borderColorValue && { borderColor: borderColorValue }),
        ...(colorValue && { color: colorValue }),
        ...sx,
      }}
    >
      {children || label}
    </MuiButton>
  );
};
